import { io, type Socket } from 'socket.io-client';
import { create } from 'zustand';
import type { NotificationItem } from '@/services/notifications.api';

/**
 * Client Socket.io singleton - connecté APRÈS login (quand on dispose d'un
 * access token). Détruit à la déconnexion.
 *
 * Le store Zustand expose :
 *  - `connect(token)` : ouvre (ou remplace) la connexion
 *  - `disconnect()` : ferme proprement
 *  - `onNotification(cb)` : s'abonne aux événements `notification` en
 *    retournant une fonction d'unsubscribe (→ utilisable en `useEffect`)
 *
 * On n'exporte PAS l'instance `Socket` directement : tout passe par le store
 * pour que les composants React n'aient jamais à s'occuper du cycle de vie.
 */

interface SocketStore {
  socket: Socket | null;
  connect: (token: string) => void;
  disconnect: () => void;
  onNotification: (cb: (n: NotificationItem) => void) => () => void;
  /**
   * Abonnement générique aux events WebSocket "mutations de domaine"
   * (opportunity.updated, task.mutated, ...). Retourne une fonction
   * d'unsubscribe à appeler dans la cleanup d'un useEffect.
   *
   * Contrairement à `onNotification`, il n'y a pas de typage serré du
   * payload : chaque event a sa propre shape et les composants consommateurs
   * décident de ce qu'ils font du payload (typiquement : refetch via API).
   */
  onEvent: <T = unknown>(event: string, cb: (payload: T) => void) => () => void;
}

export const useSocketStore = create<SocketStore>((set, get) => ({
  socket: null,

  connect: (token: string) => {
    // Ferme l'éventuel socket précédent pour éviter les connexions orphelines
    // (ex: rafraîchissement de token → reconnexion).
    const existing = get().socket;
    if (existing) {
      existing.disconnect();
    }

    const s = io({
      path: '/socket.io',
      transports: ['websocket'],
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
    });

    s.on('connect_error', (err) => {
      // N'interrompt pas l'app - les notifs REST restent disponibles
      // (polling via unread-count au besoin). On swallow silencieusement :
      // logger.warn serait bruyant pour un écart de session normal.
      void err;
    });

    set({ socket: s });
  },

  disconnect: () => {
    const s = get().socket;
    if (s) s.disconnect();
    set({ socket: null });
  },

  onNotification: (cb) => {
    const s = get().socket;
    if (!s) return () => undefined;
    const handler = (payload: NotificationItem): void => cb(payload);
    s.on('notification', handler);
    return () => {
      s.off('notification', handler);
    };
  },

  onEvent: <T = unknown>(event: string, cb: (payload: T) => void) => {
    const s = get().socket;
    if (!s) return () => undefined;
    const handler = (payload: T): void => cb(payload);
    s.on(event, handler);
    return () => {
      s.off(event, handler);
    };
  },
}));
